import api from '../../utils/api';

// Admin data access now rides the main app JWT via the shared axios instance
// (token attachment + refresh handled centrally). The backend admin endpoints
// authorize platform admins (User.role === 'admin') or active StaffAccess.
async function adminFetch(path, { method = 'GET', query, body } = {}) {
  const isCsv = path.includes('export.csv') || path.endsWith('/export');

  try {
    const res = await api.request({
      url: path,
      method,
      params: query,
      data: body,
      responseType: isCsv ? 'text' : 'json'
    });
    return res.data;
  } catch (e) {
    const err = new Error(e?.response?.data?.error || e?.message || 'Request failed');
    err.status = e?.response?.status;
    throw err;
  }
}

export const adminApi = {
  // Applications
  listPlanWaitlist: (params) => adminFetch('/api/admin/plan-waitlist', { query: params }),
  listApplications: (params) => adminFetch('/api/admin/applications', { query: params }),
  getApplication: (id) => adminFetch(`/api/admin/applications/${id}`),
  updateApplication: (id, patch) => adminFetch(`/api/admin/applications/${id}`, { method: 'PATCH', body: patch }),
  updateApplicationStatus: (id, patch) => adminFetch(`/api/admin/applications/${id}/status`, { method: 'PATCH', body: patch }),
  exportApplicationsCsv: (params) => adminFetch('/api/admin/applications/export.csv', { query: params }),
  exportApprovedMailingListCsv: () => adminFetch('/api/admin/applications/export', { query: { status: 'approved' } }),

  // Orders (Shopify)
  listOrders: (params) => adminFetch('/api/admin/orders', { query: params }),
  listIndividualOrders: () => adminFetch('/api/admin/orders/individual'),
  getOrder: (id) => adminFetch(`/api/admin/orders/${id}`),
  exportOrdersCsv: (params) => adminFetch('/api/admin/orders/export.csv', { query: params }),

  // NFC fulfilment
  getNfcSummary: () => adminFetch('/api/admin/nfc/summary'),
  getNfcUpcoming: (params) => adminFetch('/api/admin/nfc/upcoming', { query: params }),
  getNfcMismatches: (params) => adminFetch('/api/admin/nfc/mismatches', { query: params }),
  reviewMismatch: (cardId, body) => adminFetch(`/api/admin/nfc/mismatches/${encodeURIComponent(cardId)}/review`, { method: 'POST', body }),
  getInPersonCards: () => adminFetch('/api/admin/nfc/inperson'),
  markInPersonProgrammed: (cardId) => adminFetch(`/api/admin/nfc/inperson/${encodeURIComponent(cardId)}/programmed`, { method: 'PATCH' }),
  updateInPersonDistribution: (cardId, body) => adminFetch(`/api/admin/nfc/inperson/${encodeURIComponent(cardId)}/distribution`, { method: 'PATCH', body }),

  // Users
  listUsers: (params) => adminFetch('/api/admin/users', { query: params }),
  getUserDetail: (id) => adminFetch(`/api/admin/users/${id}`),
  getAnalytics: (params) => adminFetch('/api/admin/analytics', { query: params }),
  getOverview: () => adminFetch('/api/admin/overview'),

  listCardCampaigns: () => adminFetch('/api/admin/cards/campaigns'),
  createCardCampaign: (body) => adminFetch('/api/admin/cards/campaigns', { method: 'POST', body }),
  getCampaignPerformance: (id) => adminFetch(`/api/admin/cards/campaigns/${id}/performance`),
  generateCardBatch: (body) => adminFetch('/api/admin/cards/generate', { method: 'POST', body }),
  getFulfilmentQueue: () => adminFetch('/api/admin/cards/fulfilment'),
  getCardReconciliation: () => adminFetch('/api/admin/cards/reconciliation'),
  repairCardReconciliation: () => adminFetch('/api/admin/cards/reconciliation/repair', { method: 'POST' }),
  listLedgerCards: (params) => adminFetch('/api/admin/cards', { query: params }),
  exportCardBatch: (batchId) => adminFetch(`/api/admin/cards/batches/${encodeURIComponent(batchId)}/export.csv`),
  markCardProgrammed: (cardId) => adminFetch(`/api/admin/cards/${encodeURIComponent(cardId)}/programmed`, { method: 'PATCH', body: { programmed: true } }),
  reserveCard: (cardId, body) => adminFetch(`/api/admin/cards/${encodeURIComponent(cardId)}/reserve`, { method: 'POST', body }),
  revokeCard: (cardId, body) => adminFetch(`/api/admin/cards/${encodeURIComponent(cardId)}/revoke`, { method: 'POST', body }),
  reissueCard: (cardId, body) => adminFetch(`/api/admin/cards/${encodeURIComponent(cardId)}/reissue`, { method: 'POST', body }),

  listStaff: (params) => adminFetch('/api/admin/staff', { query: params }),
  grantStaff: (body) => adminFetch('/api/admin/staff', { method: 'POST', body }),
  revokeStaff: (userId) => adminFetch(`/api/admin/staff/${encodeURIComponent(userId)}/revoke`, { method: 'POST' }),
  suspendStaff: (userId) => adminFetch(`/api/admin/staff/${encodeURIComponent(userId)}/suspend`, { method: 'POST' }),
  listAuditEvents: (params) => adminFetch('/api/admin/audit-events', { query: params }),
  grantPromotion: (body) => adminFetch('/api/admin/promotions', { method: 'POST', body }),
  listCardOrders: (params) => adminFetch('/api/admin/card-orders', { query: params }),
  patchCardOrder: (orderId, body) => adminFetch(`/api/admin/card-orders/${encodeURIComponent(orderId)}`, { method: 'PATCH', body }),
};
